import { FC } from "react";
import { useRef, useState, useEffect, KeyboardEvent } from "react";
import { useAtom, useAtomValue } from "jotai";
import { studyTimeAtom, workDataAtom } from "@/atom/drawBoard";
import { message } from "../Toast";
import cls from "classnames";
import { saveWork, WorkInfo } from "@/services/api";
import styles from "./index.module.scss";
import { Tooltip } from "antd";

const LeftPanelWorkName: FC<any> = ({
  workId,
  name,
  editor,
  setEditor,
  onSave, // 可选的自定义保存回调
}: any) => {
  const [newName, setNewName] = useState("");
  const [workData, setWorkData] = useAtom(workDataAtom);
  const studyTime = useAtomValue(studyTimeAtom);

  const refInput = useRef<HTMLInputElement>(null);
  const spanRef = useRef<HTMLSpanElement>(null);
  const [isOverflow, setIsOverflow] = useState(false);

  const saveName = async () => {
    // 如果提供了自定义的 onSave 回调，使用它
    if (onSave) {
      try {
        const success = await onSave(newName);
        if (success) {
          // ✅ 成功：关闭编辑器
          setEditor(false);
        } else {
          // ✅ 失败：关闭编辑器，恢复到 props.name
          setEditor(false);
        }
      } catch (error) {
        // 异常情况：关闭编辑器，恢复到 props.name
        setEditor(false);
      }
    } else {
      // 默认行为：调用 saveWork API
      let result = await saveWork({
        workId: workId,
        workName: newName,
        situation: { timer: studyTime },
      });

      if (result.code === 0) {
        message.info("作品名称修改成功", 3000);
        setEditor(false);
      }
    }
  };

  const onFocus = () => {
    if (newName === "未命名作品") {
      refInput.current!.select();
    }
  };

  const handleSave = () => {
    if (!newName) {
      message.info("作品名称不能为空", 3000);
      // 关闭编辑器即可，恢复到 props.name
      setEditor(false);
      return;
    }
    if (newName !== name) {
      saveName();
    } else {
      setEditor(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event?.keyCode === 13 || event?.keyCode === 14 || event?.keyCode === 16 || event?.keyCode === 17) {
      handleSave();
      refInput.current?.blur();
    }
  };

  useEffect(() => {
    // ✅ 编辑模式初始化：从 name 同步到 newName
    if (editor && name) {
      setNewName(name);
    }
  }, [editor, name]);

  useEffect(() => {
    if (editor) {
      refInput.current?.focus();
    }
  }, [editor]);

  useEffect(() => {
    const span = spanRef.current;
    if (span) {
      setIsOverflow(span.scrollWidth > span.clientWidth);
    }
  }, [newName, name]);

  return (
    <div className={styles.container}>
      {editor ? (
        <div className={styles.inputBox}>
          <input
            type="input"
            ref={refInput}
            className={cls({ [styles.input]: true, [styles.notEdit]: !editor })}
            defaultValue={name}
            maxLength={50}
            value={newName}
            onFocus={onFocus}
            onBlur={handleSave}
            onKeyDown={(event) => onKeyDown(event)}
            onChange={(event) => {
              setNewName(event.target.value.replace(/^\s+|\s+$/g, ""));
            }}
          />
        </div>
      ) : (
        // 查看模式：直接显示 props.name，不使用本地状态
        <Tooltip
          title={isOverflow ? name : null}
          placement="top"
        >
          <span
            ref={spanRef}
            className={styles.name}
            style={{
              display: "inline-block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </span>
        </Tooltip>
      )}
    </div>
  );
};

export default LeftPanelWorkName;
